// W27 (2026-10-20): the "Why this record" panel on a REAL Current Action
// screen: plain-English reasons from the api-server queue item
// (current.why) for the record on screen.
//
// Renders only real strings from the api-server or an honest note, and
// is not mounted in preview mode.
//
// W40.1 (2026-10-21, M2): the "Up next" strip that lived here is gone;
// the lookahead now hydrates the approved "Up next" card
// (_shared/NextCustomer.tsx, QueueNextCustomer).
import type { ReactNode } from 'react';
import type { QueueItem, QueueStatus } from '../../_shared/useRepQueue.js';

export interface WhyThisRecordProps {
  status: QueueStatus;
  /** True when the queue's current record is the one on screen. */
  matchesRoute: boolean;
  current: QueueItem | null;
  errorCode: string | null;
}

export function WhyThisRecord(props: WhyThisRecordProps): ReactNode {
  const { status, matchesRoute, current, errorCode } = props;
  if (status === 'skipped') return null;
  let body: ReactNode;
  if (status === 'loading') {
    body = <p data-testid="why-this-record-note">Checking the queue…</p>;
  } else if (status === 'error') {
    body = <p data-testid="why-this-record-note">Queue could not load ({errorCode ?? 'ERR'}).</p>;
  } else if (current && matchesRoute && current.why.length > 0) {
    body = (
      <ul data-testid="why-this-record-list">
        {current.why.map((line, i) => (
          <li key={i} data-testid="why-this-record-item">{line}</li>
        ))}
      </ul>
    );
  } else if (current && matchesRoute) {
    body = <p data-testid="why-this-record-note">Delivered by the queue; no reasons were attached.</p>;
  } else if (current) {
    body = (
      <p data-testid="why-this-record-note">
        This record was opened from HubSpot, not from the queue. The queue is on{' '}
        <strong>{current.display?.name ?? current.customerId}</strong>; Finish &amp; Next moves there.
      </p>
    );
  } else {
    body = (
      <p data-testid="why-this-record-note">
        This record was opened from HubSpot, not from the queue. Finish &amp; Next starts the queue.
      </p>
    );
  }
  return (
    <section className="why-record" data-component="why-this-record" aria-label="Why this record">
      <span className="eyebrow">WHY THIS RECORD</span>
      {body}
    </section>
  );
}
